import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Authorization, Payment, PaymentDefinedRange, PayType } from "@asd/customer-portal-api";
import { formatDate, formatDateTime, toWallClockDate } from "@asd/customer-portal-ui";
import InfoOutlineIcon from "@mui/icons-material/InfoOutline";
import { Box, Divider, FormControlLabel, Paper, Stack, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import {
  gridClasses,
  type GridColDef,
  GridColumnHeaderParams,
  type GridRenderCellParams,
  type GridRowId,
  useGridApiRef,
} from "@mui/x-data-grid";
import { DataGrid } from "@mui/x-data-grid";
import { Allotment } from "allotment";
import { useSelector } from "react-redux";

import { useKeycloak } from "~/components/authorization";
import { FilterAutocomplete, FilterTextField } from "~/components/shared";
import { getScrambledDateString, getScrambledName, useScrambler } from "~/providers/ScramblerProvider";
import { BudgetService } from "~/services";
import { isUserParticipant } from "~/utils/keycloakHelpers";
import { splitCamelCase, USDollar } from "~/utils/stringConverters";
import { useTableExport } from "~/utils/tableExport";
import { useDashboardStaffProvider } from "../dashboard_staff/DashboardStaffProvider";

const columns: GridColDef<Payment>[] = [
  {
    field: "authReference",
    headerName: "Service #",
    width: 70,
  },
  {
    field: "serviceDate",
    headerName: "Service Date",
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "checkInDate",
    headerName: "Time In",
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDateTime,
  },
  {
    field: "checkOutDate",
    headerName: "Time Out",
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDateTime,
  },
  {
    field: "checkDate",
    headerName: "Payment Date",
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  { field: "providerName", headerName: "Service Provider", flex: 1 },
  {
    field: "unitRate",
    headerName: "Rate",
    type: "number",
    flex: 1,
    renderCell: (params: GridRenderCellParams<any, number>) => (params.value ? USDollar.format(params.value) : null),
  },
  {
    field: "claimUnits",
    headerName: "Usage",
    type: "number",
    flex: 1,
  },
  // TODO: add taxes to backend
  // { field: "checkTaxes", headerName: "Taxes", type: "number", flex: 1 },
  {
    field: "claimDollars",
    headerName: "Total Amount",
    type: "number",
    flex: 1,
    renderHeader: (params: GridColumnHeaderParams<Payment>) => (
      <Tooltip
        title={
          <Box textAlign="center">
            {params.colDef.headerName}
            <br />
            (Gross Pay - taxes not included)
          </Box>
        }
      >
        <Stack alignItems="center" direction="row" gap={0.5} overflow="hidden">
          <InfoOutlineIcon sx={{ fill: (theme) => theme.palette.custom.main, fontSize: "1rem" }} />
          <Box
            className={gridClasses.columnHeaderTitle}
            component="span"
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {params.colDef.headerName}
          </Box>
        </Stack>
      </Tooltip>
    ),
    renderCell: (params: GridRenderCellParams<any, number>) => (params.value ? USDollar.format(params.value) : null),
  },
  { field: "payType", headerName: "Pay Type", flex: 1 },
  { field: "otExempt", headerName: "OT Exempt", flex: 1 },
];

const Payments = ({ selectedAuthorizations }: { selectedAuthorizations: Authorization[] }) => {
  const theme = useTheme();
  const tableRef = useGridApiRef();
  const exportTable = useTableExport();

  const { isScramblerEnabled } = useScrambler();
  const { selectedParticipant } = useDashboardStaffProvider();
  const keycloak = useKeycloak();

  const configuration = useSelector((state: any) => state.ConfigurationSlice);

  const [isLoading, setIsLoading] = useState(false);
  const [hideFilters, setHideFilters] = useState(false);

  const [selectedPaymentId, setSelectedPaymentId] = useState<GridRowId | null>(null);

  const paymentsAbortController = useRef<AbortController>(new AbortController());
  const paymentsExportAbortController = useRef<AbortController>(new AbortController());
  const previousDataSourceState = useRef({ rows: [], rowCount: 0 });

  const [tableFilters, setTableFilters] = useReducer(
    (state, action) => ({
      ...state,
      [action.key]: action.value,
    }),
    {
      providerName: "",
      dateRange: undefined,
      payTypes: [],
    },
  );

  const dataSource = useMemo(
    () => ({
      getRows: async (params: any) => {
        paymentsAbortController.current?.abort();
        paymentsAbortController.current = new AbortController();

        if (!selectedAuthorizations.length || !tableFilters.payTypes.length) {
          return {
            rows: [],
            rowCount: 0,
          };
        }

        setIsLoading(true);

        try {
          const payments = await BudgetService.getPayments(
            selectedParticipant?.participantId,
            selectedAuthorizations.length > 1
              ? undefined
              : selectedAuthorizations
                  .map(({ authorizationServiceId }: Authorization) => authorizationServiceId)
                  .filter((id): id is string => id !== undefined),
            undefined,
            isScramblerEnabled ? undefined : tableFilters.providerName,
            tableFilters.dateRange,
            tableFilters.payTypes,
            params.paginationModel.page,
            params.paginationModel.pageSize,
            params.sortModel.map(({ field, sort }: { field: string; sort: string }) => `${field}:${sort}`),
            {
              signal: paymentsAbortController.current?.signal,
            },
          );

          if (BudgetService.getPendingRequestCount("claims") === 0) {
            (payments?.data?.items ?? []).forEach((payment: Payment) => {
              if (payment.payType === PayType.Mileage || payment.checkInDate === payment.checkOutDate) {
                payment.checkInDate = undefined;
                payment.checkOutDate = undefined;
              }

              payment.checkInDate ||= undefined;
              payment.checkOutDate ||= undefined;

              if (isScramblerEnabled) {
                payment.authReference = selectedAuthorizations.find(
                  (auth) => auth.authorizationServiceId === payment.authorizationServiceId,
                )?.authReference;
                payment.checkDate = getScrambledDateString(payment.checkDate);
                payment.checkInDate = getScrambledDateString(payment.checkInDate);
                payment.checkOutDate = getScrambledDateString(payment.checkOutDate);
                payment.providerName = getScrambledName();
                payment.serviceDate = getScrambledDateString(payment.serviceDate);
              }
            });

            let rows = payments?.data?.items ?? [];
            let rowCount = payments?.data?.total;

            if (isScramblerEnabled) {
              rows = rows.filter(({ providerName }: Payment) =>
                (providerName ?? "")
                  .trim()
                  .toLowerCase()
                  .includes((tableFilters.providerName ?? "").trim().toLowerCase()),
              );
              rowCount = rows.length;
            }

            setIsLoading(false);
            previousDataSourceState.current = {
              rows,
              rowCount,
            };
          }

          return previousDataSourceState.current;
        } catch (error: any) {
          if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
            setIsLoading(false);
          }
          throw error;
        }
      },
    }),
    [tableFilters, selectedAuthorizations, isScramblerEnabled, selectedParticipant?.participantId],
  );

  useEffect(
    () => () => {
      paymentsAbortController.current?.abort();
      paymentsAbortController.current = new AbortController();
      paymentsExportAbortController.current?.abort();
      paymentsExportAbortController.current = new AbortController();
    },
    [],
  );

  return !selectedAuthorizations.length ? null : (
    <Box sx={{ p: 1, height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Paper
        elevation={1}
        sx={[
          { position: "relative", width: "100%", height: "100%" },
          !!selectedPaymentId && {
            "& .paymentsSplit > .sash-container .sash::after": {
              content: "'\\21F3'",
              position: "absolute",
              height: "calc(var(--sash-hover-size) * 2)",
              left: "50%",
              top: "calc(50% - var(--sash-hover-size) + 1px)",
              transform: "translateX(-50%) rotate(90deg)",
              fontSize: "x-large",
              lineHeight: "6px",
            },
          },
        ]}
      >
        <Stack direction="column" height="100%">
          <Stack
            alignItems="start"
            direction="row"
            flexWrap="wrap"
            gap={1}
            overflow="auto"
            p={1}
            sx={{
              "& .MuiPickersSectionList-root": {
                p: 0,
              },
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={hideFilters}
                  onChange={() => {
                    setHideFilters((prev) => !prev);
                  }}
                  size="small"
                  sx={{ p: 0.5 }}
                />
              }
              label={<Typography>Hide Filters</Typography>}
              sx={{ minHeight: "2em" }}
            />
            <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
            <FilterTextField
              filterKey="providerName"
              filters={tableFilters}
              label="Search in Service Provider"
              setFilters={setTableFilters}
              sx={{
                flexGrow: 1,
                width: 0,
                minWidth: "10rem",

                ...(hideFilters && { display: "none" }),
              }}
            />
            <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
            <FilterAutocomplete
              defaultValue={{}}
              disableCloseOnSelect={false}
              filterKey="dateRange"
              filters={tableFilters}
              label="Filter by Service Date"
              multiple={false}
              options={Object.values(PaymentDefinedRange)}
              renderOption={(props: { key: string }, option: PaymentDefinedRange) => {
                const { key, ...optionProps } = props;
                return (
                  <Box key={key} component="li" {...optionProps}>
                    {splitCamelCase(option)}
                  </Box>
                );
              }}
              renderValue={(value: PaymentDefinedRange) => splitCamelCase(value)}
              setFilters={setTableFilters}
              sx={{
                flexGrow: 1,
                width: 0,
                minWidth: "10rem",

                ...(hideFilters && { display: "none" }),
              }}
            />
            <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
            <FilterAutocomplete
              filterKey="payTypes"
              filters={tableFilters}
              label="Filter by Pay Type"
              options={configuration.payTypes}
              setFilters={setTableFilters}
              sx={{
                flexGrow: 2,
                width: 0,
                minWidth: "10rem",

                ...(hideFilters && { display: "none" }),
              }}
            />
          </Stack>
          <Box flexGrow={1}>
            <Allotment className="paymentsSplit" minSize={15}>
              <Box height="100%" pr={1}>
                <DataGrid
                  apiRef={tableRef}
                  columns={columns}
                  dataSource={dataSource}
                  disableRowSelectionOnClick={isUserParticipant(keycloak)}
                  getRowId={({ paymentId }) => paymentId ?? ""}
                  loading={isLoading}
                  onRowSelectionModelChange={() => {
                    // TODO: implement payment details pane
                    // setSelectedPaymentId(Array.from(model.ids)[0] || null);
                    setSelectedPaymentId(null);
                  }}
                  slotProps={{
                    toolbar: {
                      label: "Payment History",
                      onExport: async (format) => {
                        paymentsExportAbortController.current?.abort();
                        paymentsExportAbortController.current = new AbortController();

                        const data = await BudgetService.exportPayments(
                          selectedParticipant?.participantId,
                          selectedAuthorizations.length > 1
                            ? undefined
                            : selectedAuthorizations
                                .map(({ authorizationServiceId }: Authorization) => authorizationServiceId)
                                .filter((id): id is string => id !== undefined),
                          undefined,
                          isScramblerEnabled ? undefined : tableFilters.providerName,
                          tableFilters.dateRange,
                          tableFilters.payTypes,
                          (tableRef.current?.getSortModel() ?? []).map(({ field, sort }) => `${field}:${sort ?? "asc"}`),
                          {
                            signal: paymentsExportAbortController.current?.signal,
                          },
                        );

                        exportTable(data, "PaymentHistory", format);
                      },
                    },
                  }}
                  slots={{
                    ...(!tableFilters.payTypes.length && {
                      noRowsOverlay: () => (
                        <Typography
                          sx={{
                            justifySelf: "center",
                            height: "100%",
                            alignContent: "center",
                          }}
                        >
                          Please select at least one Pay Type
                        </Typography>
                      ),
                    }),
                  }}
                  sx={{
                    ...(isLoading && {
                      ".MuiDataGrid-columnHeaders": {
                        pointerEvents: "none",
                      },
                    }),
                    "& .MuiDataGrid-columnHeaderTitle": {
                      color: theme.palette.custom.main,
                    },
                    "& .MuiDataGrid-toolbar .MuiDataGrid-columnHeaderTitle": {
                      ...theme.typography.h5,
                      fontWeight: theme.typography.fontWeightBold,
                    },
                  }}
                />
              </Box>
              <Allotment.Pane visible={!!selectedPaymentId}>
                <Box pl={1}>Claims Table will go here (inaccessible by Participants)</Box>
              </Allotment.Pane>
            </Allotment>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Payments;
